/**
 * Tiny, dependency-free Markdown renderer for assistant answers.
 *
 * The assistant replies in Markdown (bold, bullet/numbered lists, paragraphs).
 * We render a safe subset as React elements — no raw HTML, no dangerouslySet —
 * so headings-as-bold, bullets, and emphasis display properly instead of as
 * literal "**" and "-" characters.
 *
 * Supported: paragraphs, unordered (-, *) and ordered (1.) lists with nesting,
 * inline **bold**, *italic*, and `code`. Anything else renders as plain text.
 */
import React from "react";

// Inline: split a string into text / bold / italic / code React nodes.
function renderInline(str, keyPrefix) {
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|`([^`]+)`)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    const key = `${keyPrefix}-i${i++}`;
    if (m[2] != null || m[3] != null) {
      nodes.push(
        <strong key={key} className="font-semibold text-gray-900 dark:text-white">
          {m[2] != null ? m[2] : m[3]}
        </strong>
      );
    } else if (m[4] != null) {
      nodes.push(
        <em key={key} className="italic">
          {m[4]}
        </em>
      );
    } else if (m[5] != null) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-blue-50 px-1 py-0.5 font-mono text-[0.85em] text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        >
          {m[5]}
        </code>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

const LIST_RE = /^(\s*)(?:[-*]|(\d+)\.)\s+(.*)$/;

// Group raw text into paragraph / list blocks.
function parseBlocks(md) {
  const lines = (md || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].trim()) {
      i++;
      continue;
    }
    if (LIST_RE.test(lines[i])) {
      const items = [];
      while (i < lines.length && LIST_RE.test(lines[i])) {
        const m = lines[i].match(LIST_RE);
        items.push({ indent: m[1].length, ordered: m[2] != null, text: m[3] });
        i++;
      }
      blocks.push({ type: "list", items });
    } else {
      const buf = [];
      while (i < lines.length && lines[i].trim() && !LIST_RE.test(lines[i])) {
        buf.push(lines[i].trim());
        i++;
      }
      blocks.push({ type: "p", text: buf.join(" ") });
    }
  }
  return blocks;
}

// Render a flat list of items (with indent) into nested <ul>/<ol>.
function renderList(items, key) {
  const ordered = items[0].ordered;
  const out = [];
  let idx = 0;
  while (idx < items.length) {
    const it = items[idx];
    let j = idx + 1;
    while (j < items.length && items[j].indent > it.indent) j++;
    const children = items.slice(idx + 1, j);
    out.push(
      <li key={`${key}-${idx}`} className="pl-1">
        {renderInline(it.text, `${key}-${idx}`)}
        {children.length > 0 && renderList(children, `${key}-${idx}c`)}
      </li>
    );
    idx = j;
  }
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag
      className={`${
        ordered ? "list-decimal" : "list-disc"
      } space-y-1 pl-5 marker:text-blue-400`}
    >
      {out}
    </Tag>
  );
}

// Rendered Markdown block. `className` styles the wrapper.
export function Markdown({ text, className = "" }) {
  const blocks = parseBlocks(text);
  return (
    <div className={`space-y-2 ${className}`}>
      {blocks.map((b, i) =>
        b.type === "list" ? (
          <React.Fragment key={i}>{renderList(b.items, `l${i}`)}</React.Fragment>
        ) : (
          <p key={i} className="leading-relaxed">
            {renderInline(b.text, `p${i}`)}
          </p>
        )
      )}
    </div>
  );
}
